import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Badge } from 'reactstrap';
import { gql, useMutation } from 'urql';
import { HacpackMutation, HacpackMutationDeleteHandlingsArguments } from '../../types/graph';
import { Handling } from '../../types/graph';
import { RequireKeys } from '../../types';
import { LoadingUnitIdentifier } from '..';

export type HandlingWithIdAndUnitNumber = RequireKeys<Handling, 'id' | 'loadingUnitIdentifier'>;

type HandlingsDeleteButtonProps = ({
    multi?: false,
    handling: HandlingWithIdAndUnitNumber,
} | {
    multi: true,
    handlings: HandlingWithIdAndUnitNumber[],
}) & {
    skipPrompt?: boolean,
    disabled?: boolean,
    onBeforeDelete?: () => boolean | Promise<boolean>,
    onAfterDelete?: () => void | Promise<void>,
    onError?: (error: unknown) => void
};

type HacpackMutationDeleteHandlingsResult = Pick<HacpackMutation, 'deleteHandlings'>;

const changeHandlingPositionMutation = gql<HacpackMutationDeleteHandlingsResult, HacpackMutationDeleteHandlingsArguments>`
    mutation ($handlingIds: [Long!]!) {
        deleteHandlings(handlingIds: $handlingIds)  {
            isOk
        }
    }
`;

const HandlingsDeleteButton = ({ onAfterDelete, onBeforeDelete, onError, ...props }: HandlingsDeleteButtonProps) => {

    //const { isSuperuser, hasRoleForTerminal } = useCurrentUser(); i controlli sui permessi li lascio a chi usa il componente, magari vuole mostrare il pulsante anche a utenti che non hanno i permessi per eliminare, ma poi gestire l'errore in caso di tentativo di eliminazione senza permessi, o magari vuole fare un controllo diverso sui permessi
    const [result, deleteHandlingsHarbourOfficeApprovals] = useMutation<HacpackMutationDeleteHandlingsResult, HacpackMutationDeleteHandlingsArguments>(changeHandlingPositionMutation);
    const { error, fetching} = result;

    const [multi, handlings]: [boolean, HandlingWithIdAndUnitNumber[]] = props.multi === true ? [true, props.handlings] : [false, [props.handling]];

    const count = handlings.length;
    const disabled = props.disabled === true || count === 0 || fetching;

    const [promptOpen, setPromptOpen] = useState<boolean>(false);
    const skipPrompt = props.skipPrompt === true;

    const { t } = useTranslation('common', { keyPrefix: 'handlingsDeleteButton' });
    //const { id: handlingId, checkedIn: handlingCheckedIn } = handling; il controllo sul checkedIn lo lascio a chi usa il componente, magari vuole mostrare il pulsante anche per handling checked-in, o magari vuole fare un controllo diverso

    const togglePrompt = useCallback(() => setPromptOpen(promptOpen => !promptOpen), [setPromptOpen])

    const handlePromptButtonClick = useCallback(() => {
        setPromptOpen(true);
    }, [setPromptOpen]);

    const handleDeleteButtonClick = useCallback(async () => {
        try {
            const proceed = onBeforeDelete === undefined ? true : await onBeforeDelete();

            if (proceed === false) return;

            const args: HacpackMutationDeleteHandlingsArguments = {
                handlingIds: handlings.map(h => h.id)
            };

            const { data } = await deleteHandlingsHarbourOfficeApprovals(args)

            if (data?.deleteHandlings?.isOk !== true) {
                return;
            }

            if (onAfterDelete) await onAfterDelete();
            //window.location.reload(); QUESTO VA MESSO IN onAfterDelete, ma non è detto che si voglia ricaricare la pagina dopo ogni eliminazione, quindi lascio a chi usa il componente la decisione su cosa fare dopo l'eliminazione
        } catch (err: unknown) {
            console.error(err);
            if (onError) {
                onError(err);
            }
        } finally {
            setPromptOpen(false);
        }
    }, [deleteHandlingsHarbourOfficeApprovals, handlings, onAfterDelete, onBeforeDelete, onError]);

    const prompt = useMemo(() => {
        const count = handlings.length;

        const loadingUnits = count === 0 ? null : (
            <span>
                {handlings.map((h, i) => (
                    <React.Fragment key={h.loadingUnitIdentifier ?? i}>
                        <LoadingUnitIdentifier identifier={h.loadingUnitIdentifier} textColor='rgb(216, 17, 79)' />
                        {i < handlings.length - 1 && ', '}
                    </React.Fragment>
                ))}
            </span>
        );

        const confirm = loadingUnits === null ? null : <p><Trans
            t={t}
            i18nKey="confirmDeleteLoadingUnits"
            count={count}
            values={{ count }}
            components={[loadingUnits]}
        /></p>

        return <div>
            <Modal isOpen={!fetching && promptOpen} toggle={togglePrompt}>
                <ModalHeader>{t('confirmTitle', { count })}</ModalHeader>
                <ModalBody>{confirm}</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleDeleteButtonClick}>{t('confirm')}</Button>{' '}
                    <Button color="secondary" onClick={togglePrompt}>{t('cancel')}</Button>
                </ModalFooter>
            </Modal>
        </div>
    }, [t, handlings, handleDeleteButtonClick, promptOpen, togglePrompt, fetching])

    return <>
        <Button color='danger' disabled={disabled} type='button' onClick={skipPrompt ? handleDeleteButtonClick : handlePromptButtonClick}>
            {t('delete')}{multi && <Badge color="text">{count}</Badge>}
        </Button>
        {!skipPrompt && prompt}
    </>
};

export default HandlingsDeleteButton;